import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  isDevMode,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { BooleanInput } from '@ngx-simple/core/coercion';
import { InputBoolean } from '@ngx-simple/core/decorators';
import { SelectableWithIndex, SimSelectionDirective, SimSelectionToggleDirective } from '@ngx-simple/simple-ui/selection';
import { SimCellDefDirective, SimColumnDefDirective, SimHeaderCellDefDirective } from './cell';
import { SimTableComponent } from './table.component';

export interface RowSelectionChange<T> extends SelectableWithIndex<T> {
  checked: boolean;
}

/**
 * 如果`matSelectionMultiple`为`true`，则添加`simSelectionToggle`复选框和`simSelectionAll`复选框的列。
 *
 * 必须在父`SimSelection`指令中使用
 */
@Component({
  selector: 'sim-selection-column',
  template: `
    <ng-container simColumnDef>
      <th sim-header-cell *simHeaderCellDef class="sim-selection-column-header">
        <label
          class="sim-table-selection-label"
          *ngIf="_selection.multiple"
          simSelectionAll
          #allToggler="simSelectionAll"
          (click)="allToggler.toggle($event)"
        >
          <sim-pseudo-checkbox
            [state]="(allToggler.checked | async) ? 'checked' : (allToggler.indeterminate | async) ? 'indeterminate' : 'unchecked'"
          ></sim-pseudo-checkbox>
        </label>
      </th>
      <td sim-cell *simCellDef="let row; let i = $index" class="sim-selection-column-cell">
        <label
          class="sim-table-selection-label"
          simSelectionToggle
          #cellToggler="simSelectionToggle"
          [simSelectionToggleValue]="row"
          [simSelectionToggleIndex]="i"
          (click)="_rowClick($event, cellToggler)"
        >
          <sim-pseudo-checkbox [state]="(cellToggler.checked | async) ? 'checked' : 'unchecked'"></sim-pseudo-checkbox>
        </label>
      </td>
    </ng-container>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class SimSelectionColumnComponent<T> implements OnInit, OnDestroy {
  static ngAcceptInputType_rowStopPropagation: BooleanInput;

  /** 用于引用此列的列名 */
  @Input()
  get name(): string {
    return this._name;
  }
  set name(name: string) {
    this._name = name;

    this._syncColumnDefName();
  }
  private _name: string;

  /**
   * 列点击阻止冒泡
   */
  @Input()
  @InputBoolean<SimSelectionColumnComponent<T>, 'rowStopPropagation'>()
  rowStopPropagation: boolean;

  /** 当勾选复选框时发出的事件。 */
  @Output() readonly rowSelectionChange: EventEmitter<RowSelectionChange<T>> = new EventEmitter();

  /**
   * columnDef在'ngOnInit'期间通过静态查询提供。
   */
  @ViewChild(SimColumnDefDirective, { static: true }) _columnDef: SimColumnDefDirective;

  /**
   * cell在'ngOnInit'期间通过静态查询提供给columnDef。
   */
  @ViewChild(SimCellDefDirective, { static: true }) _cell: SimCellDefDirective;

  /**
   * headerCell在'ngOnInit'期间通过静态查询提供给columnDef。
   */
  @ViewChild(SimHeaderCellDefDirective, { static: true }) _headerCell: SimHeaderCellDefDirective;

  constructor(
    @Optional() private _table: SimTableComponent<T>,
    @Optional() @Inject(SimSelectionDirective) readonly _selection: SimSelectionDirective<T>
  ) {}

  ngOnInit() {
    if (!this._selection && isDevMode()) {
      throw Error('MatSelectionColumn: missing MatSelection in the parent');
    }

    this._syncColumnDefName();

    if (this._table) {
      this._columnDef.cell = this._cell;
      this._columnDef.headerCell = this._headerCell;
      this._table.addColumnDef(this._columnDef);
    } else if (isDevMode()) {
      throw Error('SimSelectionColumn: missing parent table');
    }
  }

  ngOnDestroy() {
    if (this._table) {
      this._table.removeColumnDef(this._columnDef);
    }
  }

  /** 点击行复选框 */
  _rowClick(event: MouseEvent, cellToggler: SimSelectionToggleDirective<T>) {
    if (this.rowStopPropagation) {
      event.stopPropagation();
    }
    cellToggler.toggle();
    const { value, index } = cellToggler;
    this.rowSelectionChange.next({
      value,
      index,
      checked: this._selection.isSelected(value, index)
    });
  }

  private _syncColumnDefName() {
    if (this._columnDef) {
      this._columnDef.name = this._name;
    }
  }
}
